import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AssignmentsAPI } from "../services/api";
import { Assignment, Score } from "../types";
import { API_URL } from "../config/api";
import { useAssignments } from "../context/AssignmentsContext";

// Info tooltip component - improved for better visibility
const InfoTooltip: React.FC<{ description?: string; criteriaName: string }> = ({
  description,
  criteriaName,
}) => {
  const [show, setShow] = useState(false);

  // Don't render anything if no description
  if (!description) return null;

  return (
    <>
      <button
        type="button"
        className="ml-2 text-purple-400 hover:text-purple-300 focus:outline-none transition-colors"
        onClick={() => setShow(true)}
        title="Click to view description"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </button>

      {/* Modal overlay for description */}
      {show && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setShow(false)}
        >
          <div
            className="bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-5 border border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-white">
                {criteriaName}
              </h3>
              <button
                onClick={() => setShow(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-line">
              {description}
            </p>
            <button
              onClick={() => setShow(false)}
              className="mt-4 w-full py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const AssignmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scores, setScores] = useState<Record<number, number>>({});
  const [comment, setComment] = useState("");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showTeamInfo, setShowTeamInfo] = useState(false);
  const [activeTab, setActiveTab] = useState<"pdf" | "video">("pdf");
  const [isEditing, setIsEditing] = useState(false); // Allow editing completed assignments
  // Private S3 file access
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [videoSignedUrl, setVideoSignedUrl] = useState<string | null>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const pdfBlobRef = useRef<string | null>(null); // For cleanup
  const [pdfZoom, setPdfZoom] = useState(1);

  // Use context for navigation and status updates
  const { getNavigation, updateAssignmentStatus, fetchAssignments, isRoundActive } =
    useAssignments();

  // Ensure assignments are loaded for navigation
  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  // Fetch current assignment detail
  useEffect(() => {
    const fetchAssignment = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        // IMPORTANT: Reset all state when loading a new assignment
        // This ensures old data from previous submission don't carry over
        setScores({});
        setComment("");
        setActiveTab("pdf");
        setIsEditing(false);
        setVideoSignedUrl(null);
        setPdfBlobUrl(null);
        setPdfZoom(1);

        const response = await AssignmentsAPI.getDetail(parseInt(id));
        if (response.success && response.data) {
          setAssignment(response.data);
          // Initialize scores from existing scores if any (for completed/in-progress assignments)
          if (response.data.scores && response.data.scores.length > 0) {
            const existingScores: Record<number, number> = {};
            response.data.scores.forEach((score: Score) => {
              existingScores[score.criteria_id] = Math.round(
                Number(score.score),
              );
            });
            setScores(existingScores);
          }
          if (response.data.comment) {
            setComment(response.data.comment);
          }
          // Set active tab based on available files
          if (
            !response.data.submission.pdf_file &&
            response.data.submission.video_file
          ) {
            setActiveTab("video");
          }
        } else {
          setError(response.message || "Failed to load assignment");
        }
      } catch (err: any) {
        setError(err.message || "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchAssignment();
  }, [id]);

  // Load PDF blob URL from private S3 via proxy endpoint
  useEffect(() => {
    if (!assignment?.submission.pdf_file) return;

    // Revoke previous blob URL
    if (pdfBlobRef.current) {
      URL.revokeObjectURL(pdfBlobRef.current);
      pdfBlobRef.current = null;
      setPdfBlobUrl(null);
    }

    setFileLoading(true);
    const token = localStorage.getItem("jury_token");
    fetch(`${API_URL.FILES.DOWNLOAD}/${assignment.submission_id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load PDF");
        return res.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        pdfBlobRef.current = url;
        setPdfBlobUrl(url);
      })
      .catch(() => {
        // Silent fail - no file or access denied
      })
      .finally(() => setFileLoading(false));

    return () => {
      if (pdfBlobRef.current) {
        URL.revokeObjectURL(pdfBlobRef.current);
        pdfBlobRef.current = null;
      }
    };
  }, [assignment?.submission_id, assignment?.submission.pdf_file]);

  // Load video signed URL on demand (called when user clicks video tab)
  const loadVideoSignedUrl = async () => {
    if (!assignment || videoSignedUrl) return;
    setFileLoading(true);
    try {
      const token = localStorage.getItem("jury_token");
      const res = await fetch(
        `${API_URL.FILES.SIGNED_URL}/${assignment.submission_id}/video/signed`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json();
      if (data.success) {
        setVideoSignedUrl(data.signed_url);
      }
    } catch {
      // Silent fail
    } finally {
      setFileLoading(false);
    }
  };

  const handleStartScoring = async () => {
    if (!assignment) return;

    try {
      setSubmitting(true);
      const response = await AssignmentsAPI.startScoring(assignment.id);
      if (response.success) {
        setAssignment((prev) =>
          prev ? { ...prev, status: "in_progress" } : prev,
        );
        updateAssignmentStatus(assignment.id, "in_progress");
      } else {
        setError(response.message || "Failed to start scoring");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  const handleScoreChange = (criteriaId: number, value: number) => {
    setScores((prev) => ({ ...prev, [criteriaId]: value }));
  };

  const handleSubmit = async () => {
    if (!assignment) return;

    // Validate all scores are filled
    const missingScores = assignment.criteria.filter(
      (c) => !scores[c.id] && scores[c.id] !== 0,
    );
    if (missingScores.length > 0) {
      setError("Please score all criteria before submitting.");
      return;
    }

    // Validate score ranges
    for (const criteria of assignment.criteria) {
      const score = scores[criteria.id];
      if (score < 1 || score > criteria.max_score) {
        setError(
          `Score for "${criteria.name}" must be between 1 and ${criteria.max_score}.`,
        );
        return;
      }
    }

    // Validate comment (required, minimum 15 words)
    const wordCount = comment.trim().split(/\s+/).filter(Boolean).length;
    if (wordCount < 15) {
      setError(
        `Please provide a comment with at least 15 words. You entered ${wordCount} word(s).`,
      );
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const scoreData: Score[] = Object.entries(scores).map(
        ([criteriaId, score]) => ({
          criteria_id: parseInt(criteriaId),
          score: score,
        }),
      );

      const response = await AssignmentsAPI.submitScore(assignment.id, {
        scores: scoreData,
        comment: comment,
      });

      if (response.success) {
        // Force refresh assignments in context to update the list
        fetchAssignments(true);

        if (isEditing) {
          // This was an update to an existing score
          setSuccessMessage("Score updated successfully!");
          setIsEditing(false);
          // Update local assignment state to reflect the changes
          setAssignment((prev) =>
            prev ? { ...prev, status: "completed" } : prev,
          );
          // Clear success message after a delay
          setTimeout(() => setSuccessMessage(null), 3000);
        } else {
          // This was a new submission
          setSuccessMessage("Score submitted successfully!");
          updateAssignmentStatus(assignment.id, "completed");

          // Get navigation BEFORE the timeout to capture current state
          const nav = getNavigation(assignment.id);
          // Use nextPending to find the next unscored assignment
          const nextUnscored = nav.nextPending || nav.next;

          // Navigate after a short delay
          setTimeout(() => {
            if (nextUnscored && nextUnscored.status !== "completed") {
              // Navigate to next unscored assignment
              navigate(`/assignment/${nextUnscored.id}`);
            } else {
              // No more unscored assignments - go to dashboard
              navigate("/dashboard");
            }
          }, 1500);
        }
      } else {
        setError(response.message || "Failed to submit score");
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  const calculateWeightedScore = () => {
    if (!assignment?.criteria) return 0;
    let weightedSum = 0;
    let totalWeight = 0;

    assignment.criteria.forEach((criterion) => {
      const score = scores[criterion.id];
      if (score) {
        const weight = criterion.weight / 100;
        const normalizedScore =
          criterion.max_score > 0 ? (score / criterion.max_score) * 10 : 0;
        weightedSum += normalizedScore * weight;
        totalWeight += weight;
      }
    });

    return totalWeight > 0 ? weightedSum : 0;
  };

  // Get navigation from context
  const nav = assignment
    ? getNavigation(assignment.id)
    : { prev: null, next: null, nextPending: null, current: 0, total: 0 };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <svg
          className="animate-spin h-12 w-12 text-purple-500"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    );
  }

  if (!assignment) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-red-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">
            {error ? "Error Loading Assignment" : "Assignment not found"}
          </h2>
          {error && <p className="text-gray-400 mb-4 text-sm">{error}</p>}
          <button
            onClick={() => navigate("/dashboard")}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const isCompleted = assignment.status === "completed";
  const roundActive = isRoundActive(assignment.round);
  // Allow editing if user clicks "Edit" button, even for completed assignments
  // But NOT if judging period has ended
  const isReadOnly = !roundActive || (isCompleted && !isEditing);
  const hasPdf = !!assignment.submission.pdf_file;
  const hasVideo = !!assignment.submission.video_file;

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col">
      {/* Compact Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-4 py-2 flex-shrink-0">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/dashboard")}
              className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-gray-700"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <div className="flex items-center space-x-3">
              <span className="px-3 py-1 bg-purple-600/20 text-purple-400 rounded-full text-sm font-medium">
                Round {assignment.round}
              </span>
              <span className="text-gray-400 text-sm">
                {nav.current} / {nav.total}
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Team Info Toggle */}
            <button
              onClick={() => setShowTeamInfo(!showTeamInfo)}
              className="flex items-center space-x-2 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm text-gray-300 transition-colors"
            >
              <span>{assignment.submission.team.team_id}</span>
              <span className="text-gray-500">|</span>
              <span className="text-gray-400">
                {assignment.submission.team.category.name}
              </span>
              <svg
                className={`w-4 h-4 transition-transform ${showTeamInfo ? "rotate-180" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {isCompleted && (
              <div className="flex items-center space-x-2">
                <span className="px-3 py-1 bg-green-600/20 text-green-400 rounded-full text-sm font-medium">
                  Completed
                </span>
                {roundActive && (
                  !isEditing ? (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="px-3 py-1 bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/30 rounded-full text-sm font-medium transition-colors flex items-center space-x-1"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      <span>Edit</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => setIsEditing(false)}
                      className="px-3 py-1 bg-gray-600/20 text-gray-400 hover:bg-gray-600/30 rounded-full text-sm font-medium transition-colors"
                    >
                      Cancel Edit
                    </button>
                  )
                )}
              </div>
            )}
            {!roundActive && (
              <span className="px-3 py-1 bg-red-600/20 text-red-400 rounded-full text-sm font-medium">
                Judging Period Ended
              </span>
            )}
          </div>
        </div>

        {/* Expandable Team Info */}
        {showTeamInfo && (
          <div className="mt-3 p-4 bg-gray-700/50 rounded-lg">
            <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div>
                <p className="text-gray-500">Team ID</p>
                <p className="text-white font-mono">
                  {assignment.submission.team.team_id}
                </p>
              </div>
              <div>
                <p className="text-gray-500">League</p>
                <p className="text-white">
                  {assignment.submission.team.league.name}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Category</p>
                <p className="text-white">
                  {assignment.submission.team.category.name}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Big Idea</p>
                <p className="text-white">
                  {assignment.submission.big_idea || "-"}
                </p>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: File Viewer */}
        <div className="flex-1 flex flex-col bg-gray-950 min-w-0">
          {/* File Tabs */}
          {(hasPdf || hasVideo) && (
            <div className="flex items-center space-x-2 p-2 bg-gray-800 border-b border-gray-700">
              {hasPdf && (
                <button
                  onClick={() => setActiveTab("pdf")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === "pdf"
                      ? "bg-purple-600 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                    <span>PDF Document</span>
                  </span>
                </button>
              )}
              {hasVideo && (
                <button
                  onClick={() => {
                    setActiveTab("video");
                    loadVideoSignedUrl();
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === "video"
                      ? "bg-purple-600 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    <span>Video</span>
                  </span>
                </button>
              )}
            </div>
          )}

          {/* File Content */}
          <div className="flex-1 relative">
            {activeTab === "pdf" &&
              hasPdf &&
              (pdfBlobUrl ? (
                <>
                  {/* Zoom Controls */}
                  <div className="absolute top-3 right-3 z-10 flex items-center space-x-1 bg-gray-800/90 backdrop-blur rounded-lg px-2 py-1.5 border border-gray-600 shadow-lg">
                    <button
                      onClick={() => setPdfZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}
                      className="p-1 text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors"
                      title="Zoom Out"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setPdfZoom(1)}
                      className="px-2 py-0.5 text-xs text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors font-mono min-w-[3rem] text-center"
                      title="Reset Zoom"
                    >
                      {Math.round(pdfZoom * 100)}%
                    </button>
                    <button
                      onClick={() => setPdfZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))}
                      className="p-1 text-gray-300 hover:text-white hover:bg-gray-700 rounded transition-colors"
                      title="Zoom In"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </div>
                  {/* PDF Viewer with zoom */}
                  <div className="absolute inset-0 overflow-auto bg-gray-950">
                    <div
                      style={{
                        width: `${pdfZoom * 100}%`,
                        height: `${pdfZoom * 100}%`,
                      }}
                    >
                      <iframe
                        src={`${pdfBlobUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                        title="PDF Document"
                        style={{
                          width: `${100 / pdfZoom}%`,
                          height: `${100 / pdfZoom}%`,
                          border: "none",
                          transform: `scale(${pdfZoom})`,
                          transformOrigin: "0 0",
                        }}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-gray-400">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500 mx-auto mb-3" />
                    <p className="text-sm">
                      {fileLoading ? "Loading PDF..." : "PDF unavailable"}
                    </p>
                  </div>
                </div>
              ))}
            {activeTab === "video" &&
              hasVideo &&
              (videoSignedUrl ? (
                <div className="absolute inset-0 flex items-center justify-center bg-black">
                  <video
                    src={videoSignedUrl}
                    controls
                    controlsList="nodownload"
                    onContextMenu={(e) => e.preventDefault()}
                    className="max-w-full max-h-full"
                    style={{ maxHeight: "100%" }}
                  >
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-gray-400">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500 mx-auto mb-3" />
                    <p className="text-sm">
                      {fileLoading
                        ? "Loading video..."
                        : "Click the Video tab to load"}
                    </p>
                  </div>
                </div>
              ))}
            {!hasPdf && !hasVideo && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <svg
                    className="w-16 h-16 mx-auto mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <p>No files available for this submission</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: Scoring Panel */}
        <div className="w-80 lg:w-96 bg-gray-800 border-l border-gray-700 flex flex-col flex-shrink-0">
          <div className="p-4 border-b border-gray-700">
            <h2 className="text-lg font-semibold text-white">Scoring</h2>
          </div>

          {/* Scrollable Criteria */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {assignment.status === "assigned" && !isReadOnly && (
              <div className="p-3 bg-yellow-900/30 border border-yellow-700/50 rounded-lg mb-4">
                <p className="text-sm text-yellow-400 mb-2">
                  Click to start scoring
                </p>
                <button
                  onClick={handleStartScoring}
                  disabled={submitting}
                  className="w-full py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 text-sm font-medium"
                >
                  {submitting ? "Starting..." : "Start Scoring"}
                </button>
              </div>
            )}

            {(assignment.status !== "assigned" || isReadOnly) && (
              <>
                {assignment.criteria.map((criteria) => (
                  <div
                    key={criteria.id}
                    className="bg-gray-700/50 rounded-lg p-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-white">
                          {criteria.name}
                        </span>
                        <InfoTooltip
                          description={criteria.description}
                          criteriaName={criteria.name}
                        />
                      </div>
                      <span className="text-xs text-gray-400">
                        {criteria.weight}%
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <input
                        type="range"
                        min="1"
                        max={criteria.max_score}
                        value={scores[criteria.id] || 1}
                        onChange={(e) =>
                          handleScoreChange(
                            criteria.id,
                            parseInt(e.target.value),
                          )
                        }
                        disabled={isReadOnly}
                        className="flex-1 h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-purple-500 disabled:cursor-not-allowed disabled:opacity-50"
                      />
                      <input
                        type="number"
                        min="1"
                        max={criteria.max_score}
                        value={scores[criteria.id] || ""}
                        onChange={(e) =>
                          handleScoreChange(
                            criteria.id,
                            parseInt(e.target.value) || 0,
                          )
                        }
                        disabled={isReadOnly}
                        className="w-14 px-2 py-1 bg-gray-600 border border-gray-500 rounded text-center text-white text-sm disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <span className="text-xs text-gray-500 w-6">
                        /{criteria.max_score}
                      </span>
                    </div>
                  </div>
                ))}

                {/* Comment */}
                <div className="pt-3">
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Comment <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    disabled={isReadOnly}
                    rows={3}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 resize-none"
                    placeholder="Please provide a comment for the contestants (minimum 15 words)"
                  />
                  <p
                    className={`text-xs mt-1 ${comment.trim().split(/\s+/).filter(Boolean).length >= 15 ? "text-green-400" : "text-gray-500"}`}
                  >
                    {comment.trim().split(/\s+/).filter(Boolean).length} / 15
                    words minimum
                  </p>
                </div>

                {/* Messages */}
                {(error || successMessage) && (
                  <div
                    className={`mt-3 px-3 py-2 rounded-lg ${error ? "bg-red-900/40 border border-red-700/50" : "bg-green-900/40 border border-green-700/50"}`}
                  >
                    <p
                      className={`text-sm ${error ? "text-red-400" : "text-green-400"}`}
                    >
                      {error || successMessage}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Score Summary & Submit */}
          {(assignment.status !== "assigned" || isReadOnly) && (
            <div className="p-4 border-t border-gray-700 bg-gray-800">
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-400 font-medium">
                  Weighted Score
                </span>
                <span className="text-2xl font-bold text-purple-400">
                  {calculateWeightedScore().toFixed(1)}
                </span>
              </div>

              {!isReadOnly && (
                <button
                  onClick={handleSubmit}
                  disabled={
                    submitting ||
                    Object.keys(scores).length !== assignment.criteria.length
                  }
                  className={`w-full py-3 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    isEditing
                      ? "bg-yellow-600 hover:bg-yellow-700"
                      : "bg-purple-600 hover:bg-purple-700"
                  }`}
                >
                  {submitting ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {isEditing ? "Updating..." : "Submitting..."}
                    </span>
                  ) : isEditing ? (
                    "Update Score"
                  ) : (
                    "Submit Score"
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
      <footer className="bg-gray-800 border-t border-gray-700 px-4 py-3 flex-shrink-0">
        <div className="flex justify-between items-center">
          <button
            onClick={() => nav.prev && navigate(`/assignment/${nav.prev.id}`)}
            disabled={!nav.prev}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              nav.prev
                ? "bg-gray-700 text-white hover:bg-gray-600"
                : "bg-gray-800 text-gray-600 cursor-not-allowed"
            }`}
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span>Previous</span>
          </button>

          <div className="flex items-center space-x-4">
            <span className="text-gray-400 text-sm">
              Assignment {nav.current} of {nav.total}
            </span>
          </div>

          <button
            onClick={() => nav.next && navigate(`/assignment/${nav.next.id}`)}
            disabled={!nav.next}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              nav.next
                ? "bg-gray-700 text-white hover:bg-gray-600"
                : "bg-gray-800 text-gray-600 cursor-not-allowed"
            }`}
          >
            <span>Next</span>
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </footer>
    </div>
  );
};

export default AssignmentDetailPage;
